import { Arg, Ctx, Mutation, Resolver, Directive } from 'type-graphql'
import { EntityManager, In } from 'typeorm'
import type { FileUpload } from 'graphql-upload/GraphQLUpload.js'
import GraphQLUpload from 'graphql-upload/GraphQLUpload.js'
import { Domain, getDataSource, getRedirectSubdomainPath, getRepository } from '@things-factory/shell'

import { thumbnail } from '../../controllers/thumbnail.js'
import { Group } from '../group/group.js'
import { Board } from './board.js'
import { BoardHistory } from './board-history.js'
import { BoardPatch, NewBoard } from './board-type.js'

async function parseJSONFile(uploadedFile: FileUpload): Promise<any> {
  var { createReadStream } = await uploadedFile

  return new Promise((resolve, reject) => {
    const chunks: Uint8Array[] = []

    createReadStream()
      .on('data', (chunk: Uint8Array) => {
        chunks.push(chunk)
      })
      .on('end', () => {
        try {
          const fileContents = Buffer.concat(chunks).toString('utf-8')
          const jsonData = JSON.parse(fileContents)
          resolve(jsonData)
        } catch (error) {
          reject(error)
        }
      })
      .on('error', (error: Error) => {
        reject(error)
      })
  })
}

@Resolver(Board)
export class BoardMutation {
  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Board, { description: 'Creates a new board.' })
  async createBoard(@Arg('board') board: NewBoard, @Ctx() context: ResolverContext): Promise<Board> {
    const { domain, user, notify, tx } = context.state
    const repository = getRepository(Board, tx)
    const groupRepository = getRepository(Group, tx)

    const oldBoard: Board = await repository.findOneBy({
      name: board.name,
      domain: { id: domain.id }
    })

    if (oldBoard) {
      throw new Error(context.t('error.board name is already taken', { name: board.name }))
    }

    const newBoard: Board = {
      ...board
    }

    newBoard.thumbnail ||=
      'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==' /* empty thumbnail */

    if (board.groupId) {
      newBoard.group = await groupRepository.findOneBy({
        id: board.groupId
      })
    }

    const created = await repository.save({
      domain,
      ...newBoard,
      state: 'draft',
      creator: user,
      updater: user
    })

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${created.name}' created`,
        body: `Board '${created.name}' created by ${user.name}\n${created.description}`,
        url: getRedirectSubdomainPath(context, domain, `/board-viewer/${created.id}`)
      })

    return created
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Board, {
    description: 'Clones a board from an existing one, potentially into a different domain.'
  })
  async cloneBoard(
    @Arg('id') id: string,
    @Arg('patch') patch: BoardPatch,
    @Arg('targetSubdomain') targetSubdomain: string,
    @Arg('targetGroupId', { nullable: true }) targetGroupId: string,
    @Ctx() context: ResolverContext
  ): Promise<Board> {
    const { domain, user, notify, tx } = context.state
    const { t } = context
    const repository = getRepository(Board, tx)

    const board = await repository.findOneBy({ domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, id })

    if (!patch.name || (patch.name == board.name && targetSubdomain == domain.subdomain)) {
      throw t('error.name must be unique from the original board', { name: patch.name })
    }

    if (targetSubdomain != domain.subdomain) {
      if ((await repository.count({ where: { domain: { subdomain: targetSubdomain }, name: patch.name } })) > 0) {
        throw t('error.name must be unique from the original board', { name: patch.name })
      }
    }

    var targetDomain = domain
    if (targetDomain && domain.subdomain != targetSubdomain) {
      targetDomain = await getRepository(Domain, tx).findOneBy({ subdomain: targetSubdomain })
      if (!targetDomain) {
        throw `given subdomain(${targetSubdomain}) not found`
      }
    }

    var targetGroup = null
    if (targetGroupId) {
      targetGroup = await getRepository(Group, tx).findOneBy({ domain: { id: targetDomain.id }, id: targetGroupId })
      if (!targetGroup) {
        throw `given group(${targetGroupId}) in domain(${targetSubdomain}) not found`
      }
    }

    const { id: excluded, ...clone } = board

    const cloned = await repository.save({
      domain: targetDomain,
      ...clone,
      ...patch,
      group: targetGroup,
      version: 0,
      state: 'draft',
      updater: user,
      creator: user
    })

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${cloned.name}' cloned`,
        body: `Board '${cloned.name}' cloned by ${user.name}\n${cloned.description}`,
        image: getRedirectSubdomainPath(context, targetDomain, `/thumbnail/${cloned.id}`),
        url: getRedirectSubdomainPath(context, targetDomain, `/board-viewer/${cloned.id}`)
      })

    return cloned
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Board, {
    description: 'Updates a board. If a model is provided, it also generates a new thumbnail.'
  })
  async updateBoard(
    @Arg('id') id: string,
    @Arg('patch') patch: BoardPatch,
    @Ctx() context: ResolverContext
  ): Promise<Board> {
    const { domain, user, notify, tx } = context.state
    const repository = getRepository(Board, tx)

    const board = await repository.findOne({
      where: { domain: { id: domain.id }, id },
      relations: ['creator']
    })

    if (!board) {
      throw new Error('board not found')
    }

    if (patch.model) {
      const thumbnailPromise = thumbnail({
        model: patch.model,
        context
      })

      try {
        const thumbnailBase64 = await Promise.race([
          thumbnailPromise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('5 seconds timeout')), 5000))
        ])

        patch.thumbnail = 'data:image/png;base64,' + thumbnailBase64.toString('base64')
      } catch (e) {
        console.warn(`Failed to get thumbnail for '${board.name}' in first 5 seconds`)
        // 5초 안에 썸네일이 생성되지 않았으므로 모델만 저장합니다.
        patch.thumbnail =
          'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==' /* empty thumbnail */

        Promise.race([
          thumbnailPromise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('Thumbnail extended timeout')), 5000))
        ])
          .then(async thumbnailBase64 => {
            /* use new resource manager */
            await getDataSource().transaction(async (tx: EntityManager) => {
              await getRepository(Board, tx).save({
                id: updated.id,
                thumbnail: 'data:image/png;base64,' + thumbnailBase64.toString('base64')
              })
            })
          })
          .catch(error => {
            console.error(`Failed to save thumbnail for '${board.name}' even after extended time:`, error)
          })
      }
    }

    const { groupId, ...patched } = patch

    if (groupId !== undefined) {
      const groupRepository = getRepository(Group, tx)
      board.group = groupId
        ? (await groupRepository.findOneBy({
            domain: { id: domain.id },
            id: groupId
          })) || null
        : null
    }

    const updated = await repository.save({
      domain,
      ...board,
      ...patched,
      state: 'draft',
      updater: user
    })

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${updated.name}' updated`,
        body: `Board '${updated.name}' updated by ${user.name}\n${updated.description}`,
        image: getRedirectSubdomainPath(context, domain, `/thumbnail/${updated.id}`),
        url: getRedirectSubdomainPath(context, domain, `/board-viewer/${updated.id}`)
      })

    return updated
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Board, { description: 'Releases a board, making it public and creating a version history.' })
  async releaseBoard(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<Board> {
    const { domain, user, notify, tx } = context.state
    const repository = getRepository(Board, tx)
    const historyRepository = getRepository(BoardHistory, tx)

    const board = await repository.findOne({
      where: { domain: { id: domain.id }, id },
      relations: ['creator']
    })

    if (!board) {
      throw `Board given id(${id}) is not found`
    }

    if (board.state == 'released') {
      throw `Board given id(${id}) is already released`
    }

    // 히스토리에서 max version 가져오기
    const maxHistory = await historyRepository
      .createQueryBuilder('history')
      .select('MAX(history.version)', 'max')
      .where('history.originalId = :id', { id: board.id })
      .getRawOne()

    const nextVersion = maxHistory?.max ? Number(maxHistory.max) + 1 : 1

    const updated = await repository.save({
      domain,
      ...board,
      version: nextVersion,
      state: 'released',
      updater: user
    })

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${updated.name}' released`,
        body: `Board '${updated.name}' released by ${user.name}\n${updated.description}`,
        image: getRedirectSubdomainPath(context, domain, `/thumbnail/${updated.id}`),
        url: getRedirectSubdomainPath(context, domain, `/board-viewer/${updated.id}`)
      })

    return updated
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Board, { description: 'Reverts a board to a specific historical version.' })
  async revertBoardVersion(
    @Arg('id') id: string,
    @Arg('version') version: number,
    @Ctx() context: ResolverContext
  ): Promise<Board> {
    const { domain, user, notify, tx } = context.state
    const repository = getRepository(Board, tx)

    const board = await repository.findOne({
      where: { domain: { id: domain.id }, id },
      relations: ['creator']
    })

    if (!board) {
      throw `Board with id(${id}) is not found`
    }

    const historyRepository = getRepository(BoardHistory, tx)

    const boardHistory = await historyRepository.findOne({
      where: { domain: { id: domain.id }, originalId: id, version },
      order: { version: 'DESC' }
    })

    if (!boardHistory) {
      throw `Board with id:version(${id}:${version}) is not found`
    }

    const updated = await repository.save({
      domain,
      ...board,
      model: boardHistory.model,
      thumbnail: boardHistory.thumbnail,
      state: 'draft',
      updater: user
    })

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${updated.name}' updated`,
        body: `Board '${updated.name}' updated by ${user.name}\n${updated.description}`,
        image: getRedirectSubdomainPath(context, domain, `/thumbnail/${updated.id}`),
        url: getRedirectSubdomainPath(context, domain, `/board-viewer/${updated.id}`)
      })

    return updated
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Boolean, { description: 'Deletes a board.' })
  async deleteBoard(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, user, notify, tx } = context.state
    const repository = getRepository(Board, tx)
    const board = await repository.findOneBy({ domain: { id: domain.id }, id })

    const deleted = await repository.softDelete(id)

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${board.name}' deleted`,
        body: `Board '${board.name}' deleted by ${user.name}\n${board.description}`
      })

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => [Board], { description: 'Imports multiple boards from JSON files.' })
  async importBoards(
    @Arg('groupId') groupId: string,
    @Arg('files', () => [GraphQLUpload]) files: FileUpload[],
    @Arg('overwrite') overwrite: boolean,
    @Ctx() context: ResolverContext
  ): Promise<Board[]> {
    const { domain, user, notify, tx } = context.state
    const groupRepository = getRepository(Group, tx)
    const boardRepository = getRepository(Board, tx)
    const group = await groupRepository.findOneBy({ domain: { id: domain.id }, id: groupId })

    if (!group) {
      throw `Group with id(${groupId}) is not found`
    }

    const boards = []

    for (const file of files) {
      const { id, name, description, model, thumbnail } = await parseJSONFile(file)

      var sameNameBoard = await boardRepository.findOneBy({ domain: { id: domain.id }, name })
      var sameIdBoard = await boardRepository.findOneBy({ id })

      if (overwrite) {
        var board = {} as any

        if (sameIdBoard) {
          if (overwrite && sameIdBoard.domainId != domain.id) {
            throw `Board with id(${id}) is already taken in another domain`
          }

          board = {
            ...sameIdBoard,
            name
          }

          if (sameNameBoard && sameIdBoard.id != sameNameBoard.id) {
            /* 이름 충돌 회피 */
            board.name = `${board.name}(${Date.now()})`
          }
        } else {
          board = {
            id,
            name: sameNameBoard ? `${name}(${Date.now()})` : name,
            version: 0,
            creator: user
          }
        }
      } else {
        board = {
          name,
          version: 0,
          creator: user
        }

        /* ID가 없으면, 사용해도 됨 */
        if (!sameIdBoard) {
          board.id = id
        }

        /* 이름 충돌 회피 */
        if (sameNameBoard) {
          board.name = `${board.name}(${Date.now()})`
        }
      }

      boards.push(
        await boardRepository.save({
          ...board,
          domain,
          description,
          model: typeof model != 'string' ? JSON.stringify(model) : model,
          thumbnail,
          group,
          state: 'draft',
          updater: user
        })
      )
    }

    notify &&
      notify({
        mode: 'in-app',
        title: `${boards.length} Board(s) are imported`,
        body: `${boards.length} Board(s) are imported into group ${group.name} by ${user.name}`
      })

    return boards
  }
}
